import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Trash2, LogOut, RefreshCw, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [galleryItems, setGalleryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'pool-fencing',
    file: null
  });

  const token = localStorage.getItem('admin_token');

  // Check authentication
  useEffect(() => {
    if (!token) {
      navigate('/admin');
    }
  }, [token, navigate]);

  // Fetch gallery items
  const fetchGallery = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/gallery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGalleryItems(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
      toast({
        title: "Error",
        description: "Failed to load gallery items",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, navigate, toast]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handleFileChange = (e) => {
    setUploadForm({
      ...uploadForm,
      file: e.target.files[0]
    });
  };

  const handleInputChange = (e) => {
    setUploadForm({
      ...uploadForm,
      [e.target.name]: e.target.value
    });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      toast({
        title: "No file selected",
        description: "Please select a file to upload",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('file', uploadForm.file);

      await axios.post(`${BACKEND_URL}/api/admin/gallery/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: "Upload successful",
        description: "Gallery item has been added"
      });

      // Reset form
      setUploadForm({
        title: '',
        description: '',
        category: 'pool-fencing',
        file: null
      });
      document.getElementById('file-input').value = '';

      // Refresh gallery
      fetchGallery();
    } catch (error) {
      toast({
        title: "Upload failed",
        description: error.response?.data?.detail || "Failed to upload file",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/gallery/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Deleted",
        description: "Gallery item has been removed"
      });

      fetchGallery();
    } catch (error) {
      toast({
        title: "Delete failed",
        description: "Failed to delete item",
        variant: "destructive"
      });
    }
  };

  const handleInstagramSync = async () => {
    setIsSyncing(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/instagram/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: "Instagram Sync Complete",
        description: response.data.message
      });

      fetchGallery();
    } catch (error) {
      toast({
        title: "Sync failed",
        description: error.response?.data?.detail || "Failed to sync Instagram",
        variant: "destructive"
      });
    } finally {
      setIsSyncing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-dashboard">
        <div className="container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <h1>Gallery Management</h1>
            <button className="btn btn-secondary" onClick={handleLogout}>
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Upload Form */}
        <div className="admin-section">
          <h2 className="admin-section-title">
            <Upload size={24} />
            Upload New Item
          </h2>
          <form onSubmit={handleUpload} className="admin-upload-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={uploadForm.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Modern Pool Fencing"
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  value={uploadForm.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="pool-fencing">Pool Fencing</option>
                  <option value="balustrade">Balustrade</option>
                  <option value="fencing">Fencing</option>
                  <option value="shower">Shower Screens</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={uploadForm.description}
                onChange={handleInputChange}
                required
                rows="3"
                placeholder="Describe the project..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="file-input">
                <ImageIcon size={18} />
                Select File * (Images or Videos)
              </label>
              <input
                type="file"
                id="file-input"
                onChange={handleFileChange}
                accept="image/*,video/*"
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload Item'}
            </button>
          </form>
        </div>

        {/* Instagram Sync */}
        <div className="admin-section">
          <h2 className="admin-section-title">
            <RefreshCw size={24} />
            Instagram Sync
          </h2>
          <p className="admin-section-desc">
            Sync latest photos and videos from your Instagram Business account (@genuine_fencers)
          </p>
          <button
            className="btn btn-secondary"
            onClick={handleInstagramSync}
            disabled={isSyncing}
          >
            <RefreshCw size={18} className={isSyncing ? 'spinning' : ''} />
            {isSyncing ? 'Syncing...' : 'Sync from Instagram'}
          </button>
        </div>

        {/* Gallery Items */}
        <div className="admin-section">
          <h2 className="admin-section-title">Gallery Items ({galleryItems.length})</h2>
          
          <div className="admin-gallery-grid">
            {galleryItems.map((item) => (
              <div key={item.id} className="admin-gallery-item">
                {item.media_type === 'VIDEO' || item.url?.endsWith('.mp4') || item.url?.endsWith('.mov') ? (
                  <video src={item.url} className="admin-gallery-preview" />
                ) : (
                  <img src={item.url} alt={item.title} className="admin-gallery-preview" />
                )}
                <div className="admin-gallery-info">
                  <h3>{item.title}</h3>
                  <p className="admin-gallery-desc">{item.description}</p>
                  <div className="admin-gallery-meta">
                    <span className="admin-badge">{item.category}</span>
                    <span className="admin-badge">{item.source}</span>
                  </div>
                </div>
                <button
                  className="admin-delete-btn"
                  onClick={() => handleDelete(item.id)}
                  title="Delete item"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>

          {galleryItems.length === 0 && (
            <p className="admin-empty">No gallery items yet. Upload some photos or sync from Instagram!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
